import { useEffect, useRef } from "react"
import type { GenerationJobRow } from "@/types/workspace"

export const CHARACTER_VIDEO_MEDIA_JOB_KINDS = [
  "character_expression_sheet",
  "character_gait_reference",
  "character_action_beat",
  "character_voice_demo",
] as const

export const CHARACTER_SHEET_JOB_KIND = "character_sheet" as const
export const CHARACTER_EMOTION_SHEET_JOB_KIND = "character_emotion_sheet" as const
export const CHARACTER_SCALE_POSE_JOB_KIND = "character_scale_pose" as const
export const SCALE_SHEET_COMPOSE_JOB_KIND = "scale_sheet_compose" as const

export function activeCharacterSheetJob(jobs: GenerationJobRow[], characterId: number): GenerationJobRow | null {
  return activeCharacterMediaJob(jobs, characterId, [CHARACTER_SHEET_JOB_KIND])
}

export function activeCharacterScalePoseJob(jobs: GenerationJobRow[], characterId: number): GenerationJobRow | null {
  return activeCharacterMediaJob(jobs, characterId, [CHARACTER_SCALE_POSE_JOB_KIND])
}

export function activeCharacterEmotionSheetJob(jobs: GenerationJobRow[], characterId: number): GenerationJobRow | null {
  return activeCharacterMediaJob(jobs, characterId, [CHARACTER_EMOTION_SHEET_JOB_KIND])
}

/** Emotion sheet generation or its sketch edit in flight. */
export function characterEmotionSheetJob(jobs: GenerationJobRow[], characterId: number): GenerationJobRow | null {
  const id = Number(characterId)
  return (
    jobs.find(
      (j) =>
        ((j.job_kind === CHARACTER_EMOTION_SHEET_JOB_KIND && Number(j.character_id) === id) ||
          (j.job_kind === "sketch_edit" && Number(j.character_id) === id && j.sketch_character_target === "emotion")) &&
        (j.status === "queued" || j.status === "processing"),
    ) ?? null
  )
}

export function characterEmotionSheetBusy(jobs: GenerationJobRow[], characterId: number) {
  return characterEmotionSheetJob(jobs, characterId) != null
}

export function activeCharacterMediaJob(
  jobs: GenerationJobRow[],
  characterId: number,
  kinds: readonly string[] = CHARACTER_VIDEO_MEDIA_JOB_KINDS,
): GenerationJobRow | null {
  const id = Number(characterId)
  return (
    jobs.find(
      (j) =>
        kinds.includes(j.job_kind) &&
        Number(j.character_id) === id &&
        (j.status === "queued" || j.status === "processing"),
    ) ?? null
  )
}

export function characterScalePoseBusy(jobs: GenerationJobRow[], characterId: number) {
  const id = Number(characterId)
  return jobs.some(
    (j) =>
      j.job_kind === CHARACTER_SCALE_POSE_JOB_KIND &&
      Number(j.character_id) === id &&
      (j.status === "queued" || j.status === "processing"),
  )
}

/** Вызывает onFinish, когда активная character media job завершилась (id пропал из списка active). */
export function useReloadOnCharacterMediaJobFinish(
  activeJob: GenerationJobRow | null,
  onFinish: () => void,
  scopeKey?: string | number,
) {
  const prevIdRef = useRef<number | null>(null)

  useEffect(() => {
    prevIdRef.current = null
  }, [scopeKey])

  useEffect(() => {
    const prev = prevIdRef.current
    const curr = activeJob?.id ?? null
    if (prev != null && prev !== curr) {
      onFinish()
    }
    prevIdRef.current = curr
  }, [activeJob?.id, onFinish])
}
